// BrandVoiceSettings — Hiring's brand guideline (the "brand voice" of 29 Oct
// 2026; design "Interview briefs & guardrails" §C, 30 Oct 2026): how every
// Hiring AI writes and speaks — the AI interviewer's tone, communications,
// the Application Helper's replies to candidates and AI-written offer
// letters (server services/brandGuideline.service). It never overrides
// EquaOS's own rules. Read by anyone who writes communications; saved by a
// recruitment administrator (GET / PUT /api/school-settings/brand-voice).
// @story MSG-SYS10

import React, { useEffect, useState } from 'react';
import api from '../../services/api';
import { Button, Input } from '../../ds';
import { Loading, Note } from '../../components/page';

const LIMIT = 2000;
const EXAMPLE = [
  'Sign every message as "HR Team, DPS Pune East".',
  'Formal but warm; address applicants by first name.',
  'Never mention fees or salary on WhatsApp.',
  'Dates as "Saturday, 28 November 2026"; times as "10:00 am".',
].join('\n');

export default function BrandVoiceSettings() {
  const [st, setSt] = useState({ phase: 'loading' });
  const [text, setText] = useState('');
  const [msg, setMsg] = useState(null);
  const [saving, setSaving] = useState(false);
  const user = (() => { try { return JSON.parse(localStorage.getItem('user') || '{}'); } catch { return {}; } })();
  const canSave = user.role === 'admin';

  useEffect(() => {
    api.get('/school-settings/brand-voice')
      .then((r) => { setSt({ phase: 'ready', ...r.data.data }); setText(r.data.data.text || ''); })
      .catch(() => setSt({ phase: 'error' }));
  }, []);

  const save = () => {
    setSaving(true);
    setMsg(null);
    api.put('/school-settings/brand-voice', { text })
      .then((r) => { setSt({ phase: 'ready', ...r.data.data }); setMsg({ tone: 'success', text: "Saved. Hiring's AI follows it from the next thing it writes; an interview follows it from its brief's next approval." }); })
      .catch((e) => setMsg({ tone: 'danger', text: e?.response?.data?.message || 'Could not save. Try again.' }))
      .finally(() => setSaving(false));
  };

  if (st.phase === 'loading') return <Loading label="Reading the brand guideline" />;
  if (st.phase === 'error') return <Note tone="warning">The brand guideline could not be read just now. Reload the page to try again.</Note>;

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <p style={{ margin: 0, color: 'var(--text-2)' }}>
        How Hiring's AI sounds at this school: how formal to be, how to sign off, words to use and words to avoid.
        It is used for everything Hiring's AI writes or says to candidates: the AI interviewer's tone, communication
        emails and WhatsApp, answers to candidates' questions, and offer letters the AI writes. One rule per line works best.
        It shapes how things are said, never what: the AI still never makes up a date, place or fee, and EquaOS's own rules always come first.
      </p>
      <Input id="brand-voice" label="Brand guideline" multiline rows={8} value={text} disabled={!canSave}
        placeholder={EXAMPLE}
        hint={`${text.length} of ${LIMIT} characters${st.updatedAt ? ` · last changed ${new Date(st.updatedAt).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' })}${st.updatedBy ? ` by ${st.updatedBy}` : ''}` : ''}`}
        onChange={(e) => setText(e.target.value.slice(0, LIMIT))} />
      {msg && <Note tone={msg.tone}>{msg.text}</Note>}
      {canSave
        ? <div><Button onClick={save} loading={saving} disabled={saving || text === (st.text || '')}>Save brand guideline</Button></div>
        : <Note tone="info">Only a recruitment administrator can change the brand guideline.</Note>}
    </div>
  );
}
